import { CandidatoService } from './candidato.service';
import { ReadCandidatoDto } from '@/api.types';
import { CursoService } from '@/modules/structure/curso/curso.service';
import { ProcessoSeletivoService } from '../processo-seletivo/processo-seletivo.service';
import { InstituicaoEnsinoService } from '@/modules/structure/instituicoes/instituicao.service';
import { toast } from 'react-toastify';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Links from '@/shared/constants/navegacao';
import api from '@/modules/api.service';
import { Endpoints } from '@/shared/constants/endpoints';
import { validaCPF } from '@/shared/utils/cpfs';

const candidatoService = new CandidatoService();
const processoSeletivoService = new ProcessoSeletivoService();
const cursoService = new CursoService();
const instituicaoEnsinoService = new InstituicaoEnsinoService();

const SITUACAO_LABELS: Record<string, string> = {
  rascunho: 'Rascunho',
  inscricao_realizada: 'Inscrição Realizada',
  inscricao_validada: 'Inscrição Validada',
  indeferida: 'Indeferida',
  classificada: 'Classificada',
  convocada: 'Convocada',
};

const RACAS_LABELS: Record<string, string> = {
  branca: 'Branca',
  preta: 'Preta',
  parda: 'Parda',
  amarela: 'Amarela',
  indigena: 'Indígena',
};

function formatarDataHoraCandidato(data: Date | string | undefined): string {
  if (!data) return '-';
  return new Date(data).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatarSituacaoCandidato(situacao: string): string {
  return SITUACAO_LABELS[situacao] ?? situacao;
}

function formatarGeneroCandidato(genero: string): string {
  return genero === 'masculino' ? 'Masculino' : genero === 'feminino' ? 'Feminino' : genero;
}

function formatarRacaCandidato(raca: string): string {
  return RACAS_LABELS[raca] ?? raca;
}

function formatarCpfExibicao(cpf: string | undefined): string {
  return cpf ? validaCPF(cpf, true) : '-';
}

function CardSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="p-0 mb-4">
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">{title}</h5>
        </div>
        <div className="card-body">{children}</div>
      </div>
    </div>
  );
}

type FileAttachmentActionsProps = {
  fileId?: string | null;
  downloadFilename: string;
  onView: (id: string) => void;
  onDownload: (id: string, filename: string) => void;
  showView?: boolean;
};

function FileAttachmentActions({
  fileId,
  downloadFilename,
  onView,
  onDownload,
  showView = true,
}: FileAttachmentActionsProps) {
  if (!fileId) return <p className="text-muted">Nenhum arquivo anexado</p>;
  return (
    <div>
      {showView && (
        <button type="button" className="btn btn-sm btn-primary me-2" onClick={() => onView(fileId)}>
          Visualizar
        </button>
      )}
      <button type="button" className="btn btn-sm btn-secondary" onClick={() => onDownload(fileId, downloadFilename)}>
        Baixar
      </button>
    </div>
  );
}

export default function CandidatoDetalhesPage() {
  const navigate = useNavigate();
  const params = useParams();
  const candidatoId = params.id ? Number(params.id) : 0;

  const [candidato, setCandidato] = useState<ReadCandidatoDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processoSeletivo, setProcessoSeletivo] = useState<{ nome: string; provaUrl?: string | null } | null>(null);
  const [cursoPretendido, setCursoPretendido] = useState<{ nome: string } | null>(null);
  const [instituicaoEnsino, setInstituicaoEnsino] = useState<{ razao_social: string } | null>(null);
  const [, setCurso] = useState<{ nome: string } | null>(null);

  useEffect(() => {
    if (candidatoId <= 0) return;
    let cancelled = false;

    const carregar = async () => {
      setIsLoading(true);
      try {
        const result = await candidatoService.findOne(candidatoId);
        if (cancelled) return;
        setCandidato(result);

        const relacionados: Promise<unknown>[] = [];

        if (result.processoSeletivoId) {
          relacionados.push(
            processoSeletivoService
              .findOne(result.processoSeletivoId)
              .then((ps) => {
                if (!cancelled) setProcessoSeletivo({ nome: ps.nome, provaUrl: ps.provaUrl });
              })
              .catch(() => { }),
          );
        }
        if (result.cursoPretendidoId) {
          relacionados.push(
            cursoService
              .findOne(result.cursoPretendidoId)
              .then((c) => {
                if (!cancelled) setCursoPretendido({ nome: c.nome });
              })
              .catch(() => { }),
          );
        }
        if (result.instituicaoEnsinoId) {
          relacionados.push(
            instituicaoEnsinoService
              .Read(result.instituicaoEnsinoId)
              .then((inst) => {
                if (!cancelled && inst) setInstituicaoEnsino({ razao_social: inst.razao_social });
              })
              .catch(() => { }),
          );
        }
        if (result.cursoId) {
          relacionados.push(
            cursoService
              .findOne(result.cursoId)
              .then((c) => {
                if (!cancelled) setCurso({ nome: c.nome });
              })
              .catch(() => { }),
          );
        }

        await Promise.all(relacionados);
      } catch (error) {
        if (!cancelled) {
          toast.error('Falha ao carregar dados do candidato: ' + error);
          navigate('/');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void carregar();
    return () => {
      cancelled = true;
    };
  }, [candidatoId, navigate]);

  const handleDownloadFile = useCallback(async (fileId: string, filename: string) => {
    try {
      const response = await api.get(Endpoints.CANDIDATO + '/file/' + fileId, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'arquivo.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao baixar arquivo.');
    }
  }, []);

  const handleViewFile = useCallback(async (fileId: string) => {
    try {
      const response = await api.get(Endpoints.CANDIDATO + '/file/' + fileId, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('Erro ao abrir arquivo.');
    }
  }, []);

  if (isLoading) {
    return (
      <div className="row">
        <div className="col-12 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!candidato) {
    return (
      <div className="row">
        <div className="col-12">
          <p>Candidato não encontrado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-8">
        <h3 className="pt-4">Detalhes do Candidato</h3>
        {processoSeletivo && (
          <span className="blockquote-footer pb-4 text-end">
            Processo Seletivo: {processoSeletivo.nome}
          </span>
        )}
      </div>
      <div className="col-md-4">
        <br />
        <Link
          type="button"
          className="btn btn-secondary float-end"
          to={`${Links.Candidato_Index}/${candidato.processoSeletivoId}`}
        >
          Voltar
        </Link>
      </div>
      <hr className="pb-4" />

      <CardSection title="Identificação Pessoal">
        <div className="row">
          <div className="mb-3 col-md-2">
            <label className="form-label fw-bold">CPF:</label>
            <p>{formatarCpfExibicao(candidato.pessoaCpf)}</p>
          </div>

          <div className="mb-3 col-md-5">
            <label className="form-label fw-bold">Nome Completo:</label>
            <p>{candidato.pessoaNome || '-'}</p>
          </div>

          <div className="mb-3 col-md-5">
            <label className="form-label fw-bold">Nome Social:</label>
            <p>{candidato.nomeSocial?.trim() ? candidato.nomeSocial : '-'}</p>
          </div>

          <div className="mb-3 col-md-2">
            <label className="form-label fw-bold">Número do Candidato:</label>
            <p>{candidato.id || '-'}</p>
          </div>

          <div className="mb-3 col-md-2">
            <label className="form-label fw-bold">Gênero:</label>
            <p>{formatarGeneroCandidato(candidato.genero)}</p>
          </div>

          <div className="mb-3 col-md-3">
            <label className="form-label fw-bold">E-mail:</label>
            <p>{candidato.pessoaEmail || '-'}</p>
          </div>

          <div className="mb-3 col-md-4">
            <label className="form-label fw-bold">Documento de Identificação:</label>
            <FileAttachmentActions
              fileId={candidato.documentoIdentificacaoUrl}
              downloadFilename="documento_identificacao.pdf"
              onView={handleViewFile}
              onDownload={handleDownloadFile}
            />
          </div>
        </div>
      </CardSection>

      <CardSection title="Dados Acadêmicos">
        <div className="row">
          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Instituição de Ensino:</label>
            <p>{instituicaoEnsino?.razao_social || candidato.instituicaoEnsinoNome || '-'}</p>
          </div>

          <div className="mb-3 col-md-3">
            <label className="form-label fw-bold">Período Atual do Curso:</label>
            <p>{candidato.periodoAtualCurso ?? '-'}</p>
          </div>

          <div className="mb-3 col-md-3">
            <label className="form-label fw-bold">Ano de Início do Curso:</label>
            <p>{candidato.anoInicioCurso ?? '-'}</p>
          </div>

          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Declaração de Matrícula:</label>
            <FileAttachmentActions
              fileId={candidato.documentoEstudoUrl}
              downloadFilename="documento_estudo.pdf"
              onView={handleViewFile}
              onDownload={handleDownloadFile}
            />
          </div>

          {candidato.provaUrl && (
            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Prova enviada pelo candidato:</label>
              <FileAttachmentActions
                fileId={candidato.provaUrl}
                downloadFilename="prova.pdf"
                onView={handleViewFile}
                onDownload={handleDownloadFile}
                showView={false}
              />
            </div>
          )}
        </div>
      </CardSection>

      <CardSection title="Endereço">
        <div className="row">
          <div className="mb-3 col-md-12">
            <label className="form-label fw-bold">Comprovante de Residência:</label>
            <FileAttachmentActions
              fileId={candidato.comprovanteResidenciaUrl}
              downloadFilename="comprovante_residencia.pdf"
              onView={handleViewFile}
              onDownload={handleDownloadFile}
            />
          </div>
        </div>
      </CardSection>

      <CardSection title="Curso e Município de Interesse">
        <div className="row">
          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Curso Pretendido:</label>
            <p>{cursoPretendido?.nome || '-'}</p>
          </div>

          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Município:</label>
            <p>{candidato.municipio || '-'}</p>
          </div>
        </div>
      </CardSection>

      <CardSection title="Identificação Étnico-Racial (Ações Afirmativas)">
        <div className="row">
          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Identificação Racial:</label>
            <p>{formatarRacaCandidato(candidato.raca)}</p>
          </div>

          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Opta por concorrer às vagas reservadas para pessoas negras?</label>
            <p>{candidato.optaVagasNegros ? 'Sim' : 'Não'}</p>
          </div>

          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Declara-se indígena?</label>
            <p>{candidato.declaraIndigena ? 'Sim' : 'Não'}</p>
          </div>

          {candidato.documentacaoIndigenaUrl && (
            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Documentação Comprobatória Indígena:</label>
              <FileAttachmentActions
                fileId={candidato.documentacaoIndigenaUrl}
                downloadFilename="documentacao_indigena.pdf"
                onView={handleViewFile}
                onDownload={handleDownloadFile}
              />
            </div>
          )}
        </div>
      </CardSection>

      <CardSection title="Pessoa com Deficiência (PcD)">
        <div className="row">
          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Opta por concorrer às vagas reservadas para pessoa com deficiência?</label>
            <p>{candidato.optaVagasPcd ? 'Sim' : 'Não'}</p>
          </div>

          {candidato.optaVagasPcd && (
            <>
              <div className="mb-3 col-md-6">
                <label className="form-label fw-bold">Descrição da deficiência:</label>
                <p>{candidato.descricaoDeficiencia || '-'}</p>
              </div>

              <div className="mb-3 col-md-6">
                <label className="form-label fw-bold">Especifique sua deficiência:</label>
                <p>{candidato.especificacaoDeficiencia || '-'}</p>
              </div>

              <div className="mb-3 col-md-6">
                <label className="form-label fw-bold">Necessita de cuidados especiais para realização da prova?</label>
                <p>{candidato.necessitaCuidadosEspeciais ? 'Sim' : 'Não'}</p>
              </div>
            </>
          )}

          {candidato.laudoMedicoUrl && (
            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Laudo Médico:</label>
              <FileAttachmentActions
                fileId={candidato.laudoMedicoUrl}
                downloadFilename="laudo_medico.pdf"
                onView={handleViewFile}
                onDownload={handleDownloadFile}
              />
            </div>
          )}
        </div>
      </CardSection>

      {candidato.documentosComplementaresUrl && (
        <CardSection title="Documentos Opcionais">
          <div className="row">
            <div className="mb-3 col-md-12">
              <label className="form-label fw-bold">Documentos Complementares Escolar:</label>
              <FileAttachmentActions
                fileId={candidato.documentosComplementaresUrl}
                downloadFilename="documentos_complementares.pdf"
                onView={handleViewFile}
                onDownload={handleDownloadFile}
              />
            </div>
          </div>
        </CardSection>
      )}

      <CardSection title="Declarações">
        <div className="row">
          <div className="mb-3 col-md-12">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={candidato.declaracaoVeracidade}
                disabled
              />
              <label className="form-check-label">
                Declaração de veracidade das informações prestadas
              </label>
            </div>
          </div>

          <div className="mb-3 col-md-12">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={candidato.confirmacaoEdital}
                disabled
              />
              <label className="form-check-label">
                Confirmação de ciência e concordância com o edital do processo seletivo
              </label>
            </div>
          </div>

          <div className="mb-3 col-md-12">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={candidato.consentimentoLgpd}
                disabled
              />
              <label className="form-check-label">
                Consentimento para tratamento de dados pessoais, nos termos da Lei Geral de Proteção de Dados (LGPD)
              </label>
            </div>
          </div>
        </div>
      </CardSection>

      <CardSection title="Informações do Sistema">
        <div className="row">
          <div className="mb-3 col-md-4">
            <label className="form-label fw-bold">Situação:</label>
            <p>{formatarSituacaoCandidato(candidato.situacao)}</p>
          </div>

          <div className="mb-3 col-md-4">
            <label className="form-label fw-bold">Data de Inscrição:</label>
            <p>{formatarDataHoraCandidato(candidato.dataInscricao)}</p>
          </div>

          <div className="mb-3 col-md-4">
            <label className="form-label fw-bold">Última Atualização:</label>
            <p>{formatarDataHoraCandidato(candidato.editadoEm)}</p>
          </div>
        </div>
      </CardSection>
    </div>
  );
}
